<script>
import Layout from '@layouts/main'
import store from '@state/store'
import CinematicLoader from '@components/cinematic-loader/cinematic-loader.vue'
import { chaveDeMontagemDaRota } from '@router/chave-de-montagem'
import { PARAMETROS } from './init-globals'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title
        ? `${title} | ${(PARAMETROS.escritorio || {}).nome || 'AAWZ'}`
        : (PARAMETROS.escritorio || {}).nome || ''
    },
  },
  components: { Layout, CinematicLoader },
  data() {
    return {
      showAlertSize: true,
      minLoaderDone: false,
    }
  },
  computed: {
    /**
     * A chave do `<RouterView>` — quem decide se trocar de URL REMONTA a tela.
     *
     * ★ [19/09/2026] Era `$route.fullPath` fixo, e `router/chave-de-montagem.js`
     * existia, testado, sem NINGUÉM o chamar: as duas rotas da Home IA
     * declaravam `meta.chaveDeMontagem` (o próprio comentário delas dizia "sem
     * isto o `:key` do app.vue destruiria o `<ViewChat>`") e a declaração era
     * ignorada. A tela remontava no instante em que a conversa ganha id — no
     * meio do primeiro turno, com o stream aberto.
     *
     * Medido na tela em 18/09 com dois envios: a remontagem cancelava o turno
     * recém-nascido (`POST /chat/<id>/cancel`) e a barra reabria a conversa
     * como aba NOVA. Opt-in: rota que não declara segue em `fullPath`, byte a
     * byte como hoje — são duas em todo o app, as duas da Home IA.
     *
     * ⚠️ [20/09/2026] Ligar isto era NECESSÁRIO e não é SUFICIENTE, e a
     * distinção custou meio dia: mesmo com a chave igual nas duas rotas, a tela
     * continuava remontando na primeira transição de URL, levando junto o anexo
     * pendente (`beforeDestroy` → `descartarFilaDeAnexos`). A causa mora no
     * router, não aqui — cada rota chamava `lazyLoadView` por conta própria e a
     * da rota nova entrava como PLACEHOLDER de async (vnode de comentário, sem
     * `tag` e sem `key`), que `sameVnode` não casa com um componente montado.
     * Ver `@utils/LazyLoad::lazyLoadViewOnce`. A chave continua indispensável:
     * sem ela, a remontagem volta pelo caminho do `fullPath`.
     */
    chaveDeMontagem() {
      return chaveDeMontagemDaRota(this.$route)
    },
    usuarioAtual() {
      return store.getters['user/currentUser'] || {}
    },
    bootLoading() {
      return this.usuarioAtual.loading || !this.minLoaderDone
    },
    // Porta declarativa por rota. Substitui o `if` de nome de rota que ficava
    // no created() com um único caso hardcoded — a intenção era liberar A
    // PÁGINA, mas como o flag era setado uma vez e nunca voltava, quem entrava
    // por ela ficava com o app INTEIRO liberado no celular pelo resto da
    // sessão. Cada rota que precisa de mobile declara `meta.allowMobile`.
    routeAllowsMobile() {
      return !!(this.$route.meta && this.$route.meta.allowMobile)
    },
    /**
     * `$route.matched` vazio ⇒ a navegação inicial ainda não resolveu.
     *
     * Enquanto isso, `$route.meta` é `{}` (`vue-router.common.js`:
     * `meta: (record && record.meta) || {}`) e TODA rota parece não permitir
     * mobile — inclusive as que declaram `allowMobile`.
     */
    rotaResolvida() {
      return !!(this.$route && this.$route.matched && this.$route.matched.length)
    },
    // `showAlertSize` passa a ser APENAS o flag de dispensa manual do usuário
    // (os dois botões do aviso) — que é o que o nome sempre quis dizer.
    blockSmallViewport() {
      // Não decidir antes de ter o dado. O app monta sem `router.onReady` e o
      // `beforeEach` aguarda `getCurrentUser()`: no cold load de
      // `/financial-planning/home` (link do assessor, bookmark, push) esse
      // intervalo é de 1-3s em 4G, e o aviso substitui até o loader — o cliente
      // recebe "acesse por um computador" logo na porta de entrada da feature
      // mobile-first. Bloquear é a decisão que precisa de prova; deixar passar
      // é reversível no tick seguinte.
      if (!this.rotaResolvida) return false

      return (
        this.$vuetify.breakpoint.smAndDown &&
        this.showAlertSize &&
        !this.routeAllowsMobile
      )
    },
  },
  created() {
    setTimeout(() => {
      this.minLoaderDone = true
    }, 1500)
    store.dispatch('user/checkIncompleteAsset')
  },
}
</script>

<template>
  <div id="app">
    <div v-if="!blockSmallViewport">
      <CinematicLoader :loading="bootLoading" />
      <Layout v-if="!bootLoading && usuarioAtual.id_interno">
        <RouterView :key="chaveDeMontagem" />
      </Layout>
    </div>

    <div
      v-if="blockSmallViewport"
      style="
        height: 100vh;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      "
      class="v-application text-center p-20"
    >
      <v-btn
        icon
        style="position: fixed; top: 20px; right: 20px; padding: 20px"
        @click="showAlertSize = false"
      >
        <icon-svg name="close" color="primary" />
      </v-btn>
      <v-icon color="primary" style="font-size: 70px"> mdi-monitor </v-icon>
      <p class="mt-10" style="margin-bottom: 30px">
        Para ter uma experiência melhor, acesse a plataforma por um computador.
      </p>
      <BaseButton primary @click="showAlertSize = false">
        Fechar Aviso
      </BaseButton>
    </div>
  </div>
</template>

<style lang="sass">
@import '~normalize.css/normalize.css'
@import '~nprogress/nprogress.css'
@import './design/index.sass'

.subhead
  margin-bottom: 8px
  font-size: rem(12px)
  font-weight: 300
  line-height: 1.3
  color: $gray-7
  font-weight: 500
  text-transform: uppercase

.subtitle
  margin-bottom: 31px
  font-size: 0.75rem
  font-weight: bold
  color: $dark

#nprogress .bar
  background: var(--v-primary-base)

#nprogress .spinner-icon
  border-top-color: var(--v-primary-base)
  border-left-color: var(--v-primary-base)

.mouse-hover
  cursor: pointer
.mouse-hover-line .v-data-table__wrapper tbody tr:hover
  cursor: pointer
</style>

<!-- This should generally be the only global CSS in the app.
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Design variables and utilities from src/design.
@import '@design';

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  background: $color-body-bg;
}
#app {
  @extend %typography-small;
}

// ===
// Base element styles
// ===

a,
a:visited {
  color: $color-link-text;
}

h1 {
  @extend %typography-xxlarge;
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: $color-link-text;
}
</style>-->
